import { FadeIn } from "./ui";
import { Share2, Fingerprint, Bot, TrendingUp, X, ArrowRight } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";

// matter-js is a sizable physics engine used only by this decorative,
// below-the-fold widget — load it in its own chunk instead of shipping it in
// every page's bundle.
const PhysicsBalloons = lazy(() =>
  import("./PhysicsBalloons").then((m) => ({ default: m.PhysicsBalloons }))
);

const services = [
  {
    title: "Branding e Posicionamento",
    description: "Desenvolvemos identidades de marca fortes e autênticas, criando estratégias de comunicação, identidade visual, manuais e experiências de marca que expressam essência, propósito e tom de voz.",
    icon: Fingerprint,
    details: {
      quote: "Uma marca sem identidade perde para quem tem — mesmo com produto pior",
      target: "Para negócios que nunca tiveram um manual de marca consistente, que cresceram e perceberam que a identidade não acompanhou, ou que querem reposicionar para um público mais qualificado.",
      deliverables: [
        "Diagnóstico de marca atual",
        "Propósito, missão, valores e posicionamento",
        "Tom de voz e identidade verbal completa",
        "Identidade visual (logo, paleta, tipografia)",
        "Manual de comunicação verbal e visual"
      ]
    }
  },
  {
    title: "IAs & Automações",
    description: "Automatizamos processos como atendimento, vendas e agendamento com soluções de IA que otimizam tempo. Desenvolvemos robôs inteligentes e personalizados para resolver o que o seu negócio precisa.",
    icon: Bot,
    details: {
      quote: "Processos que trabalham por você — mesmo quando você está fechado",
      target: "Para negócios com processos manuais repetitivos que consomem tempo da equipe. Alto volume de atendimento. Quem quer escalar sem contratar mais pessoas para funções operacionais.",
      deliverables: [
        "Mapeamento e oportunidades de automação",
        "Robôs de atendimento com IA (WhatsApp, Instagram)",
        "Automação de follow-up e funil no CRM",
        "Integração entre ferramentas",
        "Treinamento e suporte pós-implantação"
      ]
    }
  },
  {
    title: "Consultoria de Negócios",
    description: "Precisa de um acompanhamento para desenvolver seu negócio? Iremos mapear seus desafios, alinhar sua comunicação e entregar um planejamento estratégico claro para alcançar resultados reais.",
    icon: TrendingUp,
    details: {
      quote: "90 dias para sair do achismo e ter uma estratégia que você entende e consegue executar",
      target: "Para quem quer entender o negócio antes de contratar execução. Quem está estagnado e não sabe por onde começar. Quem precisa de alguém para pensar junto.",
      deliverables: [
        "Diagnóstico 360° pelo Método Prisma",
        "Planejamento estratégico completo (BSC + metas)",
        "12 sessões semanais de acompanhamento",
        "Análise competitiva e posicionamento de marca",
        "Documento final entregue ao cliente"
      ]
    }
  },
  {
    title: "Gestão de Marketing",
    description: "Se a sua empresa precisa fortalecer a marca, criar campanhas e crescer com estratégia, nós cuidamos de tudo: da comunicação à execução, incluindo planejamento, tráfego pago e direção criativa.",
    icon: Share2,
    details: {
      quote: "Seu marketing executado por quem entende o que está por trás do negócio",
      target: "Para o empresário que quer crescer mas não tem tempo — nem estrutura — para gerir marketing com estratégia. Cuidamos de tudo, do planejamento à execução.",
      deliverables: [
        "Planejamento estratégico mensal",
        "Criação de conteúdo para redes sociais",
        "Gestão de tráfego pago (Meta + Google Ads)",
        "Relatório mensal de resultados com análise",
        "Reunião mensal de alinhamento estratégico"
      ]
    }
  },
];

export function Services() {
  const [selectedService, setSelectedService] = useState<number | null>(null);

  const stickyTops = [
    "top-[100px]",
    "top-[120px]",
    "top-[140px]",
    "top-[160px]",
  ];

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (selectedService !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedService]);

  return (
    <section id="services" className="relative pb-24 pt-12 overflow-x-clip">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <FadeIn className="mb-16 md:mb-20">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-[2px] w-8 bg-brand-accent"></div>
            <span className="text-brand-accent font-bold uppercase tracking-widest text-sm">Como trabalhamos</span>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-end">
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.85]">
              Escolha como a P2A <br className="hidden md:block" />
              <span className="font-highlight italic text-brand-accent lowercase font-normal">trabalha</span> com você
            </h2>
            <p className="text-xl md:text-2xl text-brand-light/70 font-medium leading-relaxed md:pb-2">
              Cada serviço começa com diagnóstico real — porque solução sem diagnóstico é achismo.
            </p>
          </div>
        </FadeIn>

        <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            let spanClass = "";
            if (index === 0) spanClass = "md:col-span-2 lg:col-span-2";
            else if (index === 1) spanClass = "md:col-span-1 lg:col-span-1";
            else if (index === 2) spanClass = "md:col-span-1 lg:col-span-1";
            else if (index === 3) spanClass = "md:col-span-2 lg:col-span-2";

            return (
              <FadeIn key={index} delay={index * 0.1} className={`h-full ${spanClass} sticky ${stickyTops[index]} md:static z-[${10 + index}]`}>
                <div
                  onClick={() => setSelectedService(index)}
                  className="group h-full p-8 md:p-10 rounded-3xl bg-brand-light text-brand-dark hover:bg-brand-accent hover:text-brand-light transition-all duration-500 flex flex-col relative overflow-hidden shadow-xl hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_20px_40px_rgba(0,0,0,0.15)] border border-brand-dark/5 cursor-pointer"
                >
                  <div className="absolute inset-0 bg-paper opacity-40 pointer-events-none mix-blend-multiply"></div>

                  {/* Oversized Number */}
                  <div className="absolute -right-4 -top-8 text-[120px] font-highlight font-bold text-brand-dark/5 group-hover:text-brand-light/20 transition-colors duration-500 pointer-events-none">
                    0{index + 1}
                  </div>

                  <div className={`relative z-10 flex ${index === 0 ? 'flex-col md:flex-row md:items-center gap-8' : 'flex-col'} h-full`}>
                    <div className={`w-14 h-14 rounded-full bg-brand-dark/5 flex items-center justify-center text-brand-accent group-hover:bg-brand-light/20 group-hover:text-brand-light group-hover:scale-110 transition-all shrink-0 ${index === 0 ? 'mb-0' : 'mb-8'}`}>
                      <service.icon className="w-6 h-6" />
                    </div>
                    <div className="flex flex-col flex-grow">
                      <h3 className={`font-bold mb-4 font-display leading-tight ${index === 0 ? 'text-3xl md:text-5xl' : 'text-2xl md:text-3xl'}`}>{service.title}</h3>
                      <p className={`text-brand-dark/80 group-hover:text-brand-light/90 leading-relaxed flex-grow font-sans mb-6 ${index === 0 ? 'text-lg max-w-2xl' : 'text-base'}`}>
                        {service.description}
                      </p>
                      <div className="mt-auto flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-brand-accent group-hover:text-brand-light transition-colors">
                        Ver detalhes <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>

        <Suspense fallback={<div className="w-full h-[450px] md:h-[400px] mt-16 rounded-3xl bg-[#0a0a0a] border border-brand-light/10" />}>
          <PhysicsBalloons />
        </Suspense>

        <FadeIn delay={0.5} className="mt-16 flex justify-center">
          <Link
            to="/servicos"
            className="group relative inline-flex items-center justify-center px-10 py-4 text-lg font-bold text-brand-dark bg-brand-light rounded-full hover:bg-brand-accent hover:text-brand-light hover:scale-105 transition-all duration-500 overflow-hidden shadow-xl"
          >
            <span className="relative z-10 flex items-center gap-3">
              Ver todos os serviços
            </span>
          </Link>
        </FadeIn>
      </div>

      {/* Service Details Modal */}
      <AnimatePresence>
        {selectedService !== null && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedService(null)}
              className="fixed inset-0 bg-brand-dark/80 backdrop-blur-sm z-[100]"
            />
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-x-4 top-[10%] bottom-[10%] md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-3xl md:h-auto md:max-h-[85vh] bg-brand-light rounded-[2rem] z-[101] overflow-hidden flex flex-col shadow-2xl border border-brand-dark/10"
            >
              <div className="absolute inset-0 bg-paper opacity-40 pointer-events-none mix-blend-multiply"></div>

              {/* Header */}
              <div className="relative p-6 md:p-10 border-b border-brand-dark/10 flex justify-between items-start bg-brand-light z-10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-accent/10 flex items-center justify-center text-brand-accent shrink-0">
                    {(() => {
                      const Icon = services[selectedService].icon;
                      return <Icon className="w-6 h-6" />;
                    })()}
                  </div>
                  <h3 className="text-2xl md:text-4xl font-black font-display text-brand-dark leading-tight">
                    {services[selectedService].title}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedService(null)}
                  className="w-10 h-10 rounded-full bg-brand-dark/5 flex items-center justify-center text-brand-dark hover:bg-brand-accent hover:text-brand-light transition-colors shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="relative p-6 md:p-10 overflow-y-auto z-10 flex-grow">
                <div className="border-l-4 border-brand-accent pl-6 mb-10">
                  <p className="text-xl md:text-2xl font-highlight italic text-brand-dark/80 leading-relaxed">
                    "{services[selectedService].details.quote}"
                  </p>
                </div>

                <div className="mb-10">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-brand-accent mb-4">Para quem é</h4>
                  <p className="text-brand-dark/80 text-lg leading-relaxed">
                    {services[selectedService].details.target}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-bold uppercase tracking-widest text-brand-accent mb-6">O que entregamos</h4>
                  <ul className="space-y-4">
                    {services[selectedService].details.deliverables.map((item, i) => (
                      <li key={i} className="flex items-start gap-4 text-brand-dark/80 text-lg">
                        <div className="w-2 h-2 rounded-full bg-brand-accent shrink-0 mt-2.5"></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Footer CTA */}
              <div className="relative p-6 md:p-10 border-t border-brand-dark/10 bg-brand-light/50 z-10 mt-auto">
                <a
                  href={`https://wa.me/5524993144981?text=Vim%20do%20site%20e%20gostaria%20de%20saber%20mais%20sobre%20${encodeURIComponent(services[selectedService].title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-4 bg-brand-dark text-brand-light rounded-xl font-bold uppercase tracking-widest text-sm hover:bg-brand-accent transition-colors flex items-center justify-center gap-2"
                >
                  Tenho interesse neste serviço
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
